export const ROLES = {
    USER: "user",
    SUPER_ADMIN: "superAdmin",
    ADMIN: "admin",
    PARTNER: "partner",
    STORE_OWNER: "storeOwner",
    OFFICE_WORKER: "officeWorker",
};

export const OFFICE_ROLES = {
    OWNER: "owner",
    ADMIN: "admin",
    MANAGER: "manager",
    SALES: "sales",
    INSTALLER: "installer",
    DRIVER: "driver",
    WAREHOUSE: "warehouse",
    ACCOUNTANT: "accountant",
    WORKER: "worker",
};

export const OFFICE_PERMISSIONS = {
    FINANCE: "finance",
    ORDERS: "orders",
    WAREHOUSE: "warehouse",
    PRODUCTS: "products",
    CALC3D: "calc3d",
    TASKS: "tasks",
    SETTINGS: "settings",
    EMPLOYEES: "employees",
    NOTIFICATIONS: "notifications",
};

export const OFFICE_ROLE_LABELS = {
    owner: "Владелец",
    admin: "Админ офиса",
    manager: "Менеджер",
    sales: "Продажи",
    installer: "Монтажник",
    driver: "Водитель",
    warehouse: "Склад",
    accountant: "Бухгалтер",
    worker: "Сотрудник",
};

export const OFFICE_PERMISSION_LABELS = {
    finance: "Финансы",
    orders: "Заказы",
    warehouse: "Склад",
    products: "Товары",
    calc3d: "3D-калькулятор",
    tasks: "Задания",
    settings: "Настройки",
    employees: "Сотрудники",
    notifications: "Уведомления",
};

export const OFFICE_PLAN_LIMITS = {
    none: {
        maxWorkers: 0,
        maxProducts: 0,
        maxOfficeScenesPerMonth: 0,
    },
};

export function getId(value) {
    return String(value?._id || value?.id || value || "");
}

export function isAdminLike(role) {
    return [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(role);
}

export function isPartner(role) {
    return role === ROLES.PARTNER;
}

export function isStoreOwner(role) {
    return role === ROLES.STORE_OWNER;
}

export function isWorker(role) {
    return role === ROLES.OFFICE_WORKER;
}

export function isUser(role) {
    return role === ROLES.USER;
}

export function isOwnerLike(role) {
    return [
        ROLES.SUPER_ADMIN,
        ROLES.ADMIN,
        ROLES.PARTNER,
        ROLES.STORE_OWNER,
        ROLES.USER,
    ].includes(role);
}

export function normalizePermissions(permissions = {}) {
    return {
        finance: !!permissions.finance,
        orders: !!permissions.orders,
        warehouse: !!permissions.warehouse,
        products: !!permissions.products,
        // Для старых OfficeMember без calc3d сохраняем прежнее:
        // доступ к 3D совпадал с products.
        calc3d: permissions.calc3d !== undefined
            ? !!permissions.calc3d
            : !!permissions.products,
        tasks: permissions.tasks !== undefined ? !!permissions.tasks : true,
        settings: !!permissions.settings,
        employees: !!permissions.employees,
        notifications: !!permissions.notifications,
    };
}

export function isSubscriptionActive(subscription = {}) {
    if (!subscription?.validUntil) return false;
    if (subscription.type === "none") return false;
    if (subscription.status === "inactive") return false;
    if (subscription.status === "past_due") return false;

    return new Date(subscription.validUntil) > new Date();
}

export function hasActiveUserPro() {
    return false;
}

export function hasActiveOfficeSubscription(office = {}) {
    return isSubscriptionActive(office?.subscription);
}

export function getPlanName(plan) {
    if (["office_base", "office_calculator", "office_pro"].includes(plan)) return "Офисный тариф";
    return "Без подписки";
}

export function getPlanShortName(plan) {
    if (["office_base", "office_calculator", "office_pro"].includes(plan)) return "Тариф";
    return "Нет тарифа";
}

export function getPlanTone(subscription = {}) {
    if (!isSubscriptionActive(subscription)) return "inactive";
    if (subscription.status === "canceled") return "canceled";
    if (subscription.status === "past_due") return "past-due";

    return "active";
}

export function formatMoney(value) {
    return `${Number(value || 0).toLocaleString("ru-RU")} ₽`;
}

export function formatDate(value) {
    if (!value) return "—";

    return new Date(value).toLocaleDateString("ru-RU");
}

export function formatDateTime(value) {
    if (!value) return "—";

    const date = new Date(value);

    return `${date.toLocaleDateString("ru-RU")} ${date.toLocaleTimeString("ru-RU", {
        hour: "2-digit",
        minute: "2-digit",
    })}`;
}

export function getSubscriptionText(subscription = {}) {
    if (!isSubscriptionActive(subscription)) {
        return "Работа офиса приостановлена до оплаты";
    }

    const validUntil = formatDate(subscription.validUntil);

    if (subscription.status === "canceled") {
        return `Активна до ${validUntil}, автопродление отключено`;
    }

    if (subscription.status === "past_due") {
        return "Просрочена, автопродление отключено";
    }

    return `Активна до ${validUntil}`;
}

export function getOfficeRoleLabel(role) {
    return OFFICE_ROLE_LABELS[role] || "Сотрудник";
}

export function getMembershipOfficeId(membership = {}) {
    return getId(membership.officeId || membership.office || membership.officeData);
}

export function getMembershipForOffice(memberships = [], officeId) {
    const targetId = getId(officeId);

    if (!targetId || !Array.isArray(memberships)) return null;

    return memberships.find((membership) => getMembershipOfficeId(membership) === targetId) || null;
}

export function isOfficeCreatorFront({ user, office }) {
    const userId = getId(user);
    const createdById = getId(office?.createdBy);

    return !!userId && !!createdById && userId === createdById;
}

export function isOfficeAllowedUserFront({ user, office }) {
    const userId = getId(user);

    if (!userId || !Array.isArray(office?.allowedUsers)) return false;

    return office.allowedUsers.some((item) => getId(item) === userId);
}

export function isOfficeMemberAdminLike(member = {}) {
    return member?.role === OFFICE_ROLES.OWNER || member?.role === OFFICE_ROLES.ADMIN;
}

export function canUseOfficePermissionFront({
    permission,
    user,
    office,
    currentMember,
    permissions,
}) {
    if (!permission) return true;
    if (!user || !office) return false;
    if (isAdminLike(user?.role)) return true;
    if (isOfficeCreatorFront({ user, office })) return true;
    if (isOfficeMemberAdminLike(currentMember)) return true;

    const normalized = normalizePermissions(permissions || currentMember?.permissions || {});

    return !!normalized[permission];
}

export function canManageOfficeFront({
    user,
    office,
    currentMember,
    permissions,
} = {}) {
    return canUseOfficePermissionFront({
        permission: OFFICE_PERMISSIONS.SETTINGS,
        user,
        office,
        currentMember,
        permissions,
    });
}

export function canManageEmployeesFront({
    user,
    office,
    currentMember,
    permissions,
} = {}) {
    return canUseOfficePermissionFront({
        permission: OFFICE_PERMISSIONS.EMPLOYEES,
        user,
        office,
        currentMember,
        permissions,
    });
}

export function canManageOfficeFinanceFront({
    user,
    office,
    currentMember,
    permissions,
} = {}) {
    return canUseOfficePermissionFront({
        permission: OFFICE_PERMISSIONS.FINANCE,
        user,
        office,
        currentMember,
        permissions,
    });
}

export function canPayOfficeFeaturesFront({
    user,
    office,
    currentMember,
    permissions,
} = {}) {
    return canManageOfficeFinanceFront({
        user,
        office,
        currentMember,
        permissions,
    });
}

export function getOfficeFinanceAccessHint({
    canManageFinance,
    relation,
} = {}) {
    if (canManageFinance) {
        return "Можно управлять оплатой и балансом офиса.";
    }

    const roleLabel = relation?.roleLabel || "сотрудник";

    return `Оплату офиса может выполнить владелец офиса, админ офиса или сотрудник с правом «Финансы». Текущая роль: ${roleLabel}.`;
}

export function getOfficeRelation({
    user,
    office,
    membership,
    currentMember,
    permissions,
} = {}) {
    const member = currentMember || membership || null;
    const normalizedPermissions = normalizePermissions(permissions || member?.permissions || {});
    const role = member?.role || "";
    const platformAdmin = isAdminLike(user?.role);
    const creator = isOfficeCreatorFront({ user, office });
    const allowedUser = isOfficeAllowedUserFront({ user, office });

    const canSettings = canUseOfficePermissionFront({
        permission: OFFICE_PERMISSIONS.SETTINGS,
        user,
        office,
        currentMember: member,
        permissions: normalizedPermissions,
    });

    const canEmployees = canUseOfficePermissionFront({
        permission: OFFICE_PERMISSIONS.EMPLOYEES,
        user,
        office,
        currentMember: member,
        permissions: normalizedPermissions,
    });

    const hasAnyManagementPermission = [
        normalizedPermissions.settings,
        normalizedPermissions.employees,
        normalizedPermissions.finance,
        normalizedPermissions.products,
        normalizedPermissions.orders,
        normalizedPermissions.warehouse,
    ].some(Boolean);

    if (platformAdmin) {
        return {
            key: "platform-admin",
            section: "owner",
            role: "platformAdmin",
            roleLabel: "Админ платформы",
            relationLabel: "Администрирование",
            canEdit: true,
            canManageSettings: true,
            canManageEmployees: true,
            canUsePersonalPro: false,
            permissions: normalizedPermissions,
        };
    }

    if (creator) {
        return {
            key: "creator",
            section: "owner",
            role: OFFICE_ROLES.OWNER,
            roleLabel: "Владелец",
            relationLabel: "Создан мной",
            canEdit: true,
            canManageSettings: true,
            canManageEmployees: true,
            canUsePersonalPro: true,
            permissions: normalizePermissions({
                finance: true,
                orders: true,
                warehouse: true,
                products: true,
                calc3d: true,
                tasks: true,
                settings: true,
                employees: true,
                notifications: true,
            }),
        };
    }

    if (role === OFFICE_ROLES.OWNER) {
        return {
            key: "member-owner",
            section: "owner",
            role,
            roleLabel: getOfficeRoleLabel(role),
            relationLabel: "Владелец офиса",
            canEdit: true,
            canManageSettings: true,
            canManageEmployees: true,
            canUsePersonalPro: true,
            permissions: normalizedPermissions,
        };
    }

    if (role === OFFICE_ROLES.ADMIN || canSettings || canEmployees || hasAnyManagementPermission) {
        return {
            key: "managed",
            section: "managed",
            role: role || OFFICE_ROLES.MANAGER,
            roleLabel: getOfficeRoleLabel(role || OFFICE_ROLES.MANAGER),
            relationLabel: canSettings ? "Есть доступ к настройкам" : "Управление по роли",
            canEdit: !!canSettings || role === OFFICE_ROLES.ADMIN,
            canManageSettings: !!canSettings || role === OFFICE_ROLES.ADMIN,
            canManageEmployees: !!canEmployees || role === OFFICE_ROLES.ADMIN,
            canUsePersonalPro: false,
            permissions: normalizedPermissions,
        };
    }

    if (role || allowedUser) {
        return {
            key: "worker",
            section: "worker",
            role: role || OFFICE_ROLES.WORKER,
            roleLabel: getOfficeRoleLabel(role || OFFICE_ROLES.WORKER),
            relationLabel: "Доступ сотрудника",
            canEdit: false,
            canManageSettings: false,
            canManageEmployees: false,
            canUsePersonalPro: false,
            permissions: normalizedPermissions,
        };
    }

    return {
        key: "unknown",
        section: "worker",
        role: OFFICE_ROLES.WORKER,
        roleLabel: "Доступ",
        relationLabel: "Офис доступен",
        canEdit: false,
        canManageSettings: false,
        canManageEmployees: false,
        canUsePersonalPro: false,
        permissions: normalizedPermissions,
    };
}

export function getEffectiveOfficePlanInfo({
    office,
    user,
    relation,
    currentMember,
    permissions,
} = {}) {
    const subscription = office?.subscription || {};
    const officeActive = isSubscriptionActive(subscription);

    if (officeActive) {
        return {
            mode: "office",
            active: true,
            tone: getPlanTone(subscription),
            plan: subscription.plan,
            name: getPlanName(subscription.plan),
            shortName: getPlanShortName(subscription.plan),
            subscriptionText: getSubscriptionText(subscription),
            autoRenewText: subscription?.autoRenew ? "Включено" : "Отключено",
            validUntil: subscription.validUntil,
            isPersonalMode: false,
            isOfficeMode: true,
        };
    }

    const resolvedRelation = relation || getOfficeRelation({
        user,
        office,
        currentMember,
        permissions,
    });

    if (resolvedRelation?.section === "worker") {
        return {
            mode: "suspended",
            active: false,
            tone: "inactive",
            plan: subscription?.plan || "none",
            name: "Офис приостановлен",
            shortName: "Пауза",
            subscriptionText: "Работа офиса приостановлена. Возобновить Офисный тариф может владелец или сотрудник с финансовыми правами.",
            autoRenewText: "—",
            validUntil: subscription?.validUntil || null,
            isPersonalMode: false,
            isOfficeMode: false,
        };
    }

    return {
        mode: "suspended",
        active: false,
        tone: "inactive",
        plan: subscription?.plan || "none",
        name: "Офис приостановлен",
        shortName: "Пауза",
        subscriptionText: "Работа офиса приостановлена до оплаты Офисного тарифа",
        autoRenewText: "Отключено",
        validUntil: subscription?.validUntil || null,
        isPersonalMode: false,
        isOfficeMode: false,
    };
}

export function getOfficeWorkerLimit(office = {}) {
    if (!isSubscriptionActive(office?.subscription)) return 0;

    const summary = office?.billingSummary || {};
    return Math.max(0, Number(summary.maxWorkers ?? summary.settings?.maxWorkers ?? 0));
}

export function getOfficeProductLimit(office = {}) {
    return isSubscriptionActive(office?.subscription) ? null : 0;
}

export function getOfficeSceneLimit(office = {}) {
    if (!isSubscriptionActive(office?.subscription)) return 0;

    const summary = office?.billingSummary || {};
    return Math.max(0, Number(summary.includedScenes?.total ?? summary.settings?.includedScenes ?? 0));
}

export function getOfficeSceneBillingMeta(office = {}) {
    const active = isSubscriptionActive(office?.subscription);
    const summary = office?.billingSummary || {};
    const included = summary.includedScenes || {};
    const includedTotal = Math.max(0, Number(included.total ?? summary.settings?.includedScenes ?? 0));
    const includedUsed = Math.max(0, Number(included.used || 0));
    const includedRemaining = Math.max(0, Number(included.remaining ?? (includedTotal - includedUsed)));
    const packageCredits = Math.max(0, Number(summary.packageCredits || 0));
    const scenePrice = Math.max(0, Number(summary.scenePrice ?? summary.settings?.extraScenePrice ?? 0));

    let nextSource = "blocked";
    let nextSceneText = "Нужна активная подписка";
    let createTitle = "Создать 3D-сцену";
    let createSubtitle = "Нужна активная подписка";

    if (active && includedRemaining > 0) {
        nextSource = "included";
        nextSceneText = "включена в подписку";
        createSubtitle = "Включено в подписку";
    } else if (active && packageCredits > 0) {
        nextSource = "package";
        nextSceneText = "из пакета";
        createSubtitle = "Будет использована пакетная сцена";
    } else if (active) {
        nextSource = "balance";
        nextSceneText = formatMoney(scenePrice);
        createTitle = `Создать 3D-сцену — ${formatMoney(scenePrice)}`;
        createSubtitle = "С баланса офиса";
    }

    return {
        active,
        includedTotal,
        includedUsed,
        includedRemaining,
        packageCredits,
        scenePrice,
        nextSource,
        nextSceneText,
        createTitle,
        createSubtitle,
    };
}

export function getOfficeEmployeesCount(office = {}) {
    const creatorId = getId(office?.createdBy);

    if (!Array.isArray(office?.allowedUsers)) return 0;

    return new Set(office.allowedUsers
        .map((item) => getId(item))
        .filter((id) => id && id !== creatorId)).size;
}

export function getOfficeCategoriesCount(office = {}) {
    return Array.isArray(office?.categories) ? office.categories.length : 0;
}

export function getOfficeProductsCount(office = {}) {
    return Array.isArray(office?.products) ? office.products.length : 0;
}

export function getOfficeLimitRows(office = {}, context = {}) {
    const employeesCount = getOfficeEmployeesCount(office);
    const productsCount = getOfficeProductsCount(office);
    const active = isSubscriptionActive(office?.subscription);
    const workerLimit = getOfficeWorkerLimit(office);
    const sceneBilling = getOfficeSceneBillingMeta(office);

    return [
        {
            key: "workers",
            title: "Сотрудники",
            value: employeesCount,
            limit: workerLimit,
            text: active && workerLimit ? `${employeesCount}/${workerLimit}` : "Нужна подписка",
            percent: active && workerLimit ? Math.min((employeesCount / workerLimit) * 100, 100) : 0,
            locked: !active,
            note: active && workerLimit ? `Доступно мест: ${Math.max(0, workerLimit - employeesCount)}` : "Добавление сотрудников заблокировано",
        },
        {
            key: "products",
            title: "Товары",
            value: productsCount,
            limit: null,
            text: active ? "Без лимита" : "Офис приостановлен",
            percent: active ? 100 : 0,
            locked: !active,
            note: active ? `${productsCount} в офисе` : "Работа с товарами заблокирована до оплаты",
        },
        {
            key: "scenes",
            title: "3D-сцены",
            value: sceneBilling.includedUsed,
            limit: sceneBilling.includedTotal,
            text: active
                ? `Использовано: ${sceneBilling.includedUsed} / ${sceneBilling.includedTotal}`
                : "Нужна подписка",
            percent: active && sceneBilling.includedTotal
                ? Math.min((sceneBilling.includedUsed / sceneBilling.includedTotal) * 100, 100)
                : 0,
            locked: !active,
            details: active
                ? [
                    `Осталось: ${sceneBilling.includedRemaining}`,
                    `Пакетных: ${sceneBilling.packageCredits}`,
                    `Следующая сцена: ${sceneBilling.nextSceneText}`,
                ]
                : ["Создание новых сцен заблокировано"],
        },
    ];
}

export function canCreateOfficeFront(user = {}) {
    return !!getId(user);
}

export function getCreateOfficeHint(user = {}) {
    return canCreateOfficeFront(user) ? "" : "Войдите в аккаунт, чтобы создать офис.";
}

export function getBackendError(err, fallback = "Ошибка") {
    return err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.payload?.error ||
        err?.message ||
        fallback;
}
